import { useRef } from "react";
import type { Note, Notebook } from "@fletchy/core";
import { Download, FileInput, LayoutPanelLeft, Plus, RotateCcw, Tag } from "lucide-react";

interface SidebarProps {
  isDemoMode: boolean;
  notes: Note[];
  notebooks: Notebook[];
  tags: string[];
  selectedNotebookId: string | "all";
  selectedTag: string | null;
  renamingNotebookId: string | null;
  notebookDraftTitle: string;
  onCreateNotebook(): void;
  onExportVault(): void;
  onResetVault(): void;
  onImportFile(file: File): void;
  onSelectAllNotes(): void;
  onSelectNotebook(notebookId: string): void;
  onToggleTag(tag: string): void;
  onNotebookDraftTitleChange(title: string): void;
  onCommitNotebookRename(): void;
  onCancelNotebookRename(): void;
}

export function Sidebar({
  isDemoMode,
  notes,
  notebooks,
  tags,
  selectedNotebookId,
  selectedTag,
  renamingNotebookId,
  notebookDraftTitle,
  onCreateNotebook,
  onExportVault,
  onResetVault,
  onImportFile,
  onSelectAllNotes,
  onSelectNotebook,
  onToggleTag,
  onNotebookDraftTitleChange,
  onCommitNotebookRename,
  onCancelNotebookRename,
}: SidebarProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <aside className="sidebar">
      <div className="brandRow">
        <div className="appMark">F</div>
        <div>
          <strong>Fletchy</strong>
          <span>{isDemoMode ? "Demo vault" : "Local vault"}</span>
        </div>
      </div>

      <div className="toolbarGrid">
        <button type="button" onClick={onCreateNotebook}>
          <Plus size={15} /> Notebook
        </button>
        <button type="button" onClick={() => fileInputRef.current?.click()}>
          <FileInput size={15} /> Import
        </button>
        <button type="button" onClick={onExportVault}>
          <Download size={15} /> Export
        </button>
        <button type="button" onClick={onResetVault}>
          <RotateCcw size={15} /> Reset
        </button>
      </div>

      <input
        ref={(element) => {
          fileInputRef.current = element;
        }}
        className="hiddenInput"
        type="file"
        accept=".fletchy,.zip,.qvlibrary,application/json"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          if (file) {
            onImportFile(file);
            event.currentTarget.value = "";
          }
        }}
      />

      <section className="navSection">
        <div className="sectionTitle">Notebooks</div>
        <button
          type="button"
          className={selectedNotebookId === "all" ? "navItem active" : "navItem"}
          onClick={onSelectAllNotes}
        >
          <LayoutPanelLeft size={15} />
          All Notes
          <span>{notes.length}</span>
        </button>
        {notebooks.map((notebook) => {
          const noteCount = notes.filter((note) => note.notebookId === notebook.id).length;
          const isActive = selectedNotebookId === notebook.id;

          if (renamingNotebookId === notebook.id) {
            return (
              <div key={notebook.id} className={isActive ? "navItem notebookRenameItem active" : "navItem notebookRenameItem"}>
                <LayoutPanelLeft size={15} />
                <input
                  className="notebookRenameInput"
                  value={notebookDraftTitle}
                  onBlur={onCommitNotebookRename}
                  onChange={(event) => onNotebookDraftTitleChange(event.target.value)}
                  onFocus={(event) => event.currentTarget.select()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      onCommitNotebookRename();
                    }

                    if (event.key === "Escape") {
                      event.preventDefault();
                      onCancelNotebookRename();
                    }
                  }}
                  aria-label="Notebook name"
                  autoFocus
                />
                <span>{noteCount}</span>
              </div>
            );
          }

          return (
            <button
              type="button"
              key={notebook.id}
              className={isActive ? "navItem active" : "navItem"}
              onClick={() => onSelectNotebook(notebook.id)}
            >
              <LayoutPanelLeft size={15} />
              {notebook.title}
              <span>{noteCount}</span>
            </button>
          );
        })}
      </section>

      <section className="navSection">
        <div className="sectionTitle">Tags</div>
        {tags.length === 0 ? <p className="emptyHint">No tags yet</p> : null}
        {tags.map((tag) => (
          <button
            type="button"
            key={tag}
            className={selectedTag === tag ? "navItem active" : "navItem"}
            onClick={() => onToggleTag(tag)}
          >
            <Tag size={15} />
            {tag}
            <span>{notes.filter((note) => note.tags.includes(tag)).length}</span>
          </button>
        ))}
      </section>
    </aside>
  );
}
